import React, { useState } from "react";
import PropTypes from "prop-types";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
	TextField
} from "@mui/material";
import { isEmpty } from "lodash";
import useApi from "../hooks/useApi";

/**
 * EnvironmentAddModal component allows users to add a new environment.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Object} props.config - Configuration object.
 * @param {Object} props.config.uri - URI configuration object.
 * @param {string} props.config.uri.base - Base URL for API requests.
 * @param {boolean} props.isOpen - Flag indicating whether the modal is open.
 * @param {function} props.onChangeBulk - Function to update parent state changes.
 * @param {string|number} props.applicationId - ID of the application to which the environment belongs.
 * @returns {React.Element} The EnvironmentAddModal component.
 */
const EnvironmentAddModal = (props) => {
	const [formValues, setFormValues] = useState({ environmentAddName: "" });
	const [errors, setErrors] = useState({});
	const { config, isOpen, onChangeBulk: parentOnChangeBulk, applicationId } = props;
	const { environment } = useApi();

	/**
	 * Resets the form values and errors, then closes the modal.
	 *
	 * @function handleClose
	 */
	const handleClose = () => {
		setErrors({});
		setFormValues({ environmentAddName: "" });
		parentOnChangeBulk({ environmentAddModalIsOpen: false });
	};

	/**
	 * Validates inputs, submits new environment data to the server via useApi, and fetches updated environments.
	 *
	 * @async
	 * @function handleSubmit
	 */
	const handleSubmit = async () => {
		const newErrors = {};
		if (!formValues.environmentAddName) {
			newErrors.environmentAddName = "You must supply an environment name";
		}
		if (!isEmpty(newErrors)) {
			setErrors(newErrors);
			return;
		}
		try {
			await environment.create({
				data: {
					name: formValues.environmentAddName,
					applicationId: applicationId
				}
			});
			const environmentsData = await environment.search({
				queryParams: { applicationId: applicationId }
			});
			setFormValues({ environmentAddName: "" });
			parentOnChangeBulk({
				environmentAddModalIsOpen: false,
				environments: environmentsData.rows
			});
		} catch (error) {
			console.error("Error creating environment", error);
		}
	};

	return (
		<Dialog open={isOpen} onClose={handleClose}>
			<DialogTitle>Add New Environment</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Add a new environment with the following details
				</DialogContentText>
				<TextField
					autoFocus
					margin="dense"
					id="environmentAddName"
					label="Environment Name"
					type="text"
					fullWidth
					value={formValues.environmentAddName}
					onChange={(e) =>
						setFormValues({ ...formValues, environmentAddName: e.target.value })
					}
					error={Boolean(errors.environmentAddName)}
					helperText={errors.environmentAddName}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSubmit} color="primary">
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

EnvironmentAddModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	applicationId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired
};

export default EnvironmentAddModal;
